/**
 * Cart Page
 *
 * Displays the shopping cart with items, quantities, and checkout button.
 * Accessible at /s/:storeSlug/cart
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection, type LanguageCode } from '../../i18n/languages';
import StoreFooter from '../../components/storefront/StoreFooter';
import CookieConsent from '../../components/storefront/CookieConsent';

interface Store {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  theme: StoreTheme;
  currency: string;
  locale: LanguageCode;
}

interface StoreTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
}

interface CartItem {
  productId: string;
  productName: string;
  variantId?: string;
  variantName?: string;
  price: number;
  quantity: number;
  image?: string;
}

const defaultTheme: StoreTheme = {
  primaryColor: '#0d9488',
  secondaryColor: '#6366f1',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
};

/**
 * Safely parse JSON from localStorage with fallback
 */
function safeParseJSON<T>(json: string | null, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    console.warn('Failed to parse JSON from localStorage, using fallback');
    return fallback;
  }
}

export default function CartPage() {
  const { storeSlug } = useParams<{ storeSlug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('store');
  const [store, setStore] = useState<Store | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadStore() {
      try {
        setLoading(true);

        // Load store data
        const response = await fetch(`/api/public/stores/${storeSlug}`);
        if (!response.ok) {
          setError('Store not found');
          return;
        }

        const data = await response.json() as { store: Store };
        setStore(data.store);

        // Load cart from localStorage
        const cartKey = `cart_${data.store.id}`;
        const cart = safeParseJSON<CartItem[]>(localStorage.getItem(cartKey), []);
        setCartItems(cart);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load store');
      } finally {
        setLoading(false);
      }
    }

    if (storeSlug) {
      loadStore();
    }
  }, [storeSlug]);

  const theme = store?.theme || defaultTheme;

  // Change language when store locale is set
  useEffect(() => {
    if (store?.locale && store.locale !== i18n.language) {
      i18n.changeLanguage(store.locale);
    }
  }, [store?.locale, i18n]);

  const updateQuantity = (index: number, quantity: number) => {
    if (!store) return;

    const newCart = [...cartItems];
    if (quantity <= 0) {
      newCart.splice(index, 1);
    } else {
      newCart[index].quantity = quantity;
    }

    setCartItems(newCart);
    localStorage.setItem(`cart_${store.id}`, JSON.stringify(newCart));
  };

  const removeItem = (index: number) => {
    if (!store) return;

    const newCart = [...cartItems];
    newCart.splice(index, 1);

    setCartItems(newCart);
    localStorage.setItem(`cart_${store.id}`, JSON.stringify(newCart));
  };

  const clearCart = () => {
    if (!store) return;

    setCartItems([]);
    localStorage.removeItem(`cart_${store.id}`);
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const storeLocale = store?.locale || 'en';
  const storeDirection = getLanguageDirection(storeLocale);

  const formatPrice = (price: number, currency: string) => {
    const localeMap: Record<string, string> = {
      en: 'en-US', zh: 'zh-CN', hi: 'hi-IN', es: 'es-ES', fr: 'fr-FR',
      ar: 'ar-SA', bn: 'bn-BD', pt: 'pt-BR', ru: 'ru-RU', ja: 'ja-JP',
      de: 'de-DE', ko: 'ko-KR', vi: 'vi-VN', tr: 'tr-TR', it: 'it-IT',
      th: 'th-TH', pl: 'pl-PL', nl: 'nl-NL', id: 'id-ID', he: 'he-IL',
    };
    const intlLocale = localeMap[storeLocale] || 'en-US';
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('storefront.storeNotFound')}</h1>
          <p className="text-gray-500 mb-6">{t('storefront.storeNotFoundDescription')}</p>
          <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium">
            {t('storefront.goToHomepage')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={storeDirection} lang={storeLocale}>
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to={`/s/${store.slug}`} className="flex items-center gap-3">
                {store.logo_url ? (
                  <img src={store.logo_url} alt={store.name} className="h-10 w-auto" />
                ) : (
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {store.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-xl font-bold">{store.name}</span>
              </Link>

              <Link
                to={`/s/${store.slug}`}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                {t('cart.continueShopping')}
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-8">{t('cart.shoppingCart')}</h1>

          {cartItems.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl shadow-sm">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('cart.emptyCart')}</h2>
              <p className="text-gray-500 mb-6">{t('cart.emptyCartDescription')}</p>
              <Link
                to={`/s/${store.slug}`}
                className="inline-block px-6 py-3 rounded-lg text-white font-medium"
                style={{ backgroundColor: theme.primaryColor }}
              >
                {t('cart.startShopping')}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item, index) => (
                  <div key={`${item.productId}-${item.variantId}`} className="bg-white rounded-xl shadow-sm p-4 flex gap-4">
                    {/* Image */}
                    <div className="w-24 h-24 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                      {item.image ? (
                        <img src={item.image} alt={item.productName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900">{item.productName}</h3>
                      {item.variantName && (
                        <p className="text-sm text-gray-500">{item.variantName}</p>
                      )}
                      <p className="text-lg font-semibold mt-1" style={{ color: theme.primaryColor }}>
                        {formatPrice(item.price, store.currency)}
                      </p>
                    </div>

                    {/* Quantity & Remove */}
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeItem(index)}
                        className="text-gray-400 hover:text-red-500 p-1"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          className="w-8 h-8 border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          className="w-8 h-8 border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Clear Cart */}
                <button
                  onClick={clearCart}
                  className="text-sm text-gray-500 hover:text-red-500"
                >
                  {t('cart.clearCart')}
                </button>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('checkout.orderSummary')}</h2>

                  <div className="space-y-3 pb-4 border-b border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('cart.subtotal')} ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} {cartItems.reduce((sum, item) => sum + item.quantity, 0) === 1 ? t('storefront.product') : t('storefront.products')})</span>
                      <span className="font-medium">{formatPrice(subtotal, store.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('cart.shipping')}</span>
                      <span className="text-gray-500">{t('cart.calculatedAtCheckout')}</span>
                    </div>
                  </div>

                  <div className="flex justify-between py-4 text-lg font-semibold">
                    <span>{t('cart.total')}</span>
                    <span style={{ color: theme.primaryColor }}>
                      {formatPrice(subtotal, store.currency)}
                    </span>
                  </div>

                  <button
                    onClick={() => navigate(`/s/${store.slug}/checkout`)}
                    className="w-full py-3 rounded-lg text-white font-medium"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {t('cart.proceedToCheckout')}
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    {t('cart.taxesShippingCalculated', 'Taxes and shipping calculated at checkout')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Footer */}
        <StoreFooter store={store} direction={storeDirection} />

        {/* Cookie Consent */}
        <CookieConsent
          storeId={store.id}
          storeSlug={store.slug}
          primaryColor={theme.primaryColor}
        />
      </div>
  );
}
